import useUrl from './helpers/useUrl'

/**
 * @param {import('@nuxtjs/axios').NuxtAxiosInstance} client
 * */
export default (client) => {
  const root = useUrl('raffle/v2')

  return {
    /**
     * @param {Object} GetRaffleListV2DTO
     * @param {number} GetRaffleListV2DTO.limit
     * @param {number} GetRaffleListV2DTO.offset
     * @param {string} GetRaffleListV2DTO.status
     *
     * @returns {Promise<AxiosResponse<GetRaffleListV2Response>>}
    * */
    getRaffleListV2({ limit, offset, status }) {
      const url = root('list')
      const params = {
        status,
        limit,
        offset,
      }
      return client.get(url, { params })
    },
    /**
     * @param {Object} GetRaffleV2DTO
     * @param {string} GetRaffleV2DTO.raffleTerm
     *
     * @returns {Promise<AxiosResponse<GetRaffleV2Response>>}
    * */
    getRaffleV2({ raffleTerm }) {
      const url = root(`info/${raffleTerm}`)

      return client.get(url)
    },
    /**
     * @param {Object} GetRaffleUserRelatedV2DTO
     * @param {string} GetRaffleUserRelatedV2DTO.raffleTerm
     *
     * @returns {Promise<AxiosResponse<GetRaffleUserRelatedV2Response>>}
    * */
    getRaffleUserRelatedV2({ raffleTerm }) {
      const url = root(`user/info/${raffleTerm}`)

      return client.get(url)
    },
    /**
     * @param {Object} GetRaffleTicketsV2DTO
     * @param {string} GetRaffleTicketsV2DTO.raffleTerm
     * @param {number} GetRaffleTicketsV2DTO.limit
     * @param {number} GetRaffleTicketsV2DTO.offset
     *
     * @returns {Promise<AxiosResponse<GetRaffleTicketsV2Response>>}
    * */
    getRaffleTicketsV2({ raffleTerm, limit, offset }) {
      const url = root(`tickets/${raffleTerm}`)
      const params = {
        limit,
        offset,
      }
      return client.get(url, { params })
    },
  }
}

/** @typedef {object} GetRaffleListV2Response
 * @property {object[]} raffles
 * @property {number} raffles.id
 * @property {string} raffles.name
 * @property {string} raffles.url
 * @property {string} raffles.create_date
 * @property {number} raffles.places_count
 * @property {string} raffles.start_date
 * @property {string} raffles.end_date
 * @property {number} raffles.ticket_price
 * @property {string} raffles.status
 * @property {string} raffles.type
 * @property {string} raffles.video_url
 * @property {object} raffles.images
 * @property {string} raffles.images.coin_image
 * @property {string} raffles.images.background1
 * @property {string} raffles.images.background2
 * @property {string} raffles.images.main_banner_logo_mobile
 * @property {string} raffles.images.main_banner_image_mobile
 * @property {string} raffles.images.main_banner_logo_desktop
 * @property {string} raffles.images.main_banner_image_desktop
 * @property {string} raffles.rules_url
 * @property {object} raffles.seo
 * @property {object} raffles.seo.og
 * @property {object} raffles.seo.og.ru
 * @property {string} raffles.seo.og.ru.url
 * @property {string} raffles.seo.og.ru.type
 * @property {string} raffles.seo.og.ru.image
 * @property {string} raffles.seo.og.ru.title
 * @property {string} raffles.seo.og.ru.image_alt
 * @property {string} raffles.seo.og.ru.site_name
 * @property {string} raffles.seo.og.ru.description
 * @property {object} raffles.seo.og.uk
 * @property {string} raffles.seo.og.uk.url
 * @property {string} raffles.seo.og.uk.type
 * @property {string} raffles.seo.og.uk.image
 * @property {string} raffles.seo.og.uk.title
 * @property {string} raffles.seo.og.uk.image_alt
 * @property {string} raffles.seo.og.uk.site_name
 * @property {string} raffles.seo.og.uk.description
 * @property {object} raffles.seo.twitter
 * @property {object} raffles.seo.twitter.ru
 * @property {string} raffles.seo.twitter.ru.url
 * @property {string} raffles.seo.twitter.ru.card
 * @property {string} raffles.seo.twitter.ru.site
 * @property {string} raffles.seo.twitter.ru.image
 * @property {string} raffles.seo.twitter.ru.title
 * @property {string} raffles.seo.twitter.ru.creator
 * @property {string} raffles.seo.twitter.ru.image_alt
 * @property {string} raffles.seo.twitter.ru.description
 * @property {object} raffles.seo.twitter.uk
 * @property {string} raffles.seo.twitter.uk.url
 * @property {string} raffles.seo.twitter.uk.card
 * @property {string} raffles.seo.twitter.uk.site
 * @property {string} raffles.seo.twitter.uk.image
 * @property {string} raffles.seo.twitter.uk.title
 * @property {string} raffles.seo.twitter.uk.creator
 * @property {string} raffles.seo.twitter.uk.image_alt
 * @property {string} raffles.seo.twitter.uk.description
 * @property {number} raffles.prize_fund
 * @property {string} raffles.term
 * @property {string} raffles.currency
 * @property {object} raffles.relativeDates
 * @property {number} raffles.relativeDates.startsIn
 * @property {number} raffles.relativeDates.endsIn
 * @property {object} raffles.subraffle
 * @property {number} raffles.subraffle.id
 * @property {number} raffles.subraffle.places_count
 * @property {string} raffles.subraffle.near_date
 * @property {number} raffles.subraffle.prize
 * @property {number} raffles.subraffle.prizeFund
 * @property {number} raffles.subraffle.relativeDate
 * @property {string} raffles.subraffle.status
 * @property {object} raffles.text
 * @property {object} raffles.text.uk
 * @property {string} raffles.text.uk.schedule_text
 * @property {string} raffles.text.uk.header
 * @property {string} raffles.text.uk.instruction
 * @property {string} raffles.text.uk.localized_description
 * @property {object} raffles.text.ru
 * @property {string} raffles.text.ru.schedule_text
 * @property {string} raffles.text.ru.instruction
 * @property {string} raffles.text.ru.header
 * @property {string} raffles.text.ru.localized_description
 * @property {object[]|} raffles.winners
 * @property {string} raffles.winners.name
 * @property {number} raffles.winners.user_id
 * @property {number} raffles.winners.ticketsCount
 * @property {number} raffles.raf_id
 * @property {} raffles.images_url
 * @property {object[]|} raffles.subraffles
 * @property {number} raffles.subraffles.id
 * @property {number} raffles.subraffles.places_count
 * @property {string} raffles.subraffles.near_date
 * @property {number} raffles.subraffles.prize
 * @property {number} raffles.subraffles.prizeFund
 * @property {number} raffles.subraffles.relativeDate
 * @property {string} raffles.subraffles.status
 * @property {number} raffles.relativeDates.availableUntil
 * @property {number} totalCount
 */

/** @typedef {object} GetRaffleV2Response
 * @property {number} id
 * @property {string} name
 * @property {string} url
 * @property {string} create_date
 * @property {number} places_count
 * @property {string} start_date
 * @property {string} end_date
 * @property {number} ticket_price
 * @property {string} status
 * @property {string} type
 * @property {string} video_url
 * @property {object} images
 * @property {string} images.coin_image
 * @property {string} images.background1
 * @property {string} images.background2
 * @property {string} images.main_banner_logo_mobile
 * @property {string} images.main_banner_image_mobile
 * @property {string} images.main_banner_logo_desktop
 * @property {string} images.main_banner_image_desktop
 * @property {string} rules_url
 * @property {object} seo
 * @property {object} seo.og
 * @property {object} seo.og.ru
 * @property {string} seo.og.ru.url
 * @property {string} seo.og.ru.type
 * @property {string} seo.og.ru.image
 * @property {string} seo.og.ru.title
 * @property {string} seo.og.ru.image_alt
 * @property {string} seo.og.ru.site_name
 * @property {string} seo.og.ru.description
 * @property {object} seo.og.uk
 * @property {string} seo.og.uk.url
 * @property {string} seo.og.uk.type
 * @property {string} seo.og.uk.image
 * @property {string} seo.og.uk.title
 * @property {string} seo.og.uk.image_alt
 * @property {string} seo.og.uk.site_name
 * @property {string} seo.og.uk.description
 * @property {object} seo.twitter
 * @property {object} seo.twitter.ru
 * @property {string} seo.twitter.ru.url
 * @property {string} seo.twitter.ru.card
 * @property {string} seo.twitter.ru.site
 * @property {string} seo.twitter.ru.image
 * @property {string} seo.twitter.ru.title
 * @property {string} seo.twitter.ru.creator
 * @property {string} seo.twitter.ru.image_alt
 * @property {string} seo.twitter.ru.description
 * @property {object} seo.twitter.uk
 * @property {string} seo.twitter.uk.url
 * @property {string} seo.twitter.uk.card
 * @property {string} seo.twitter.uk.site
 * @property {string} seo.twitter.uk.image
 * @property {string} seo.twitter.uk.title
 * @property {string} seo.twitter.uk.creator
 * @property {string} seo.twitter.uk.image_alt
 * @property {string} seo.twitter.uk.description
 * @property {number} prize_fund
 * @property {string} term
 * @property {string} currency
 * @property {object} relativeDates
 * @property {number} relativeDates.startsIn
 * @property {number} relativeDates.endsIn
 * @property {object} subraffle
 * @property {number} subraffle.id
 * @property {number} subraffle.places_count
 * @property {string} subraffle.near_date
 * @property {number} subraffle.prize
 * @property {number} subraffle.prizeFund
 * @property {number} subraffle.relativeDate
 * @property {string} subraffle.status
 * @property {object} text
 * @property {object} text.uk
 * @property {string} text.uk.schedule_text
 * @property {string} text.uk.header
 * @property {string} text.uk.instruction
 * @property {string} text.uk.localized_description
 * @property {object} text.ru
 * @property {string} text.ru.schedule_text
 * @property {string} text.ru.instruction
 * @property {string} text.ru.header
 * @property {string} text.ru.localized_description
 * @property {} winners
 * @property {number} raf_id
 * @property {} images_url
 * @property {object[]|undefined} subraffles
 * @property {number} subraffles.id
 * @property {number} subraffles.places_count
 * @property {string} subraffles.near_date
 * @property {number} subraffles.prize
 * @property {number} subraffles.prizeFund
 * @property {number} subraffles.relativeDate
 * @property {string} subraffles.status
 */

/** @typedef {object} GetRaffleUserRelatedV2Response
 * @property {object} user
 * @property {number} user.coins
 * @property {object} user.tickets
 * @property {object} user.tickets.count
 * @property {number} user.tickets.count.won
 * @property {number} user.tickets.count.other
 * @property {number} user.tickets.count.total
 * @property {object} raffle
 * @property {} raffle.winners
 * @property {object[]|undefined} subraffles
 * @property {number} subraffles.id
 * @property {string} subraffles.date
 * @property {number} subraffles.raffle_id
 * @property {number} subraffles.places_count
 * @property {number} subraffles.prize
 * @property {number} subraffles.prize_fund
 * @property {null} subraffles.vip_super_prize_fund
 * @property {null} subraffles.vip_top_players_count
 * @property {} subraffles.winners
 * @property {string} subraffles.status
 * @property {number} subraffles.relativeDate
 */

/** @typedef {object} GetRaffleTicketsV2Response
 * @property {object} tickets
 * @property {} tickets.won
 * @property {} tickets.other
 * @property {boolean} status
 */
